import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { applyThemePalette, getTheme } from '../themes';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeContext = createContext({
  hospitalThemeKey: 'teal-navy',
  bloodBankThemeKey: 'blood-red',
  diagnosticThemeKey: 'diagnostic-cyan',
  savedHospitalThemeKey: 'teal-navy',
  savedBloodBankThemeKey: 'blood-red',
  savedDiagnosticThemeKey: 'diagnostic-cyan',
  previewHospital: () => {},
  previewBloodBank: () => {},
  previewDiagnostic: () => {},
  commitSavedThemes: () => {},
  refreshFromServer: () => {},
});

const STORAGE_KEY = 'skb_theme_cache_v2';

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Theme cache read failed:', e);
    }
    return {};
  }
};

const writeCache = (data) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Theme cache write failed:', e);
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const cached = readCache();

  // "Saved" = source of truth from server. Mirror to public visitors.
  const [savedHospitalThemeKey, setSavedHospitalThemeKey] = useState(cached.active_theme || 'teal-navy');
  const [savedBloodBankThemeKey, setSavedBloodBankThemeKey] = useState(cached.blood_bank_theme || 'blood-red');
  const [savedDiagnosticThemeKey, setSavedDiagnosticThemeKey] = useState(cached.diagnostic_theme || 'diagnostic-cyan');

  // "Applied" = currently rendered. Differs from saved during admin live-preview.
  const [hospitalThemeKey, setHospitalThemeKey] = useState(savedHospitalThemeKey);
  const [bloodBankThemeKey, setBloodBankThemeKey] = useState(savedBloodBankThemeKey);
  const [diagnosticThemeKey, setDiagnosticThemeKey] = useState(savedDiagnosticThemeKey);

  // Track whether we've ever loaded fresh data from server
  const hydratedRef = useRef(false);

  // Apply hospital theme to <html> root whenever it changes
  useEffect(() => {
    applyThemePalette(hospitalThemeKey);
  }, [hospitalThemeKey]);

  const refreshFromServer = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const data = res.data || {};
      const next = {
        active_theme: data.active_theme || 'teal-navy',
        blood_bank_theme: data.blood_bank_theme || 'blood-red',
        diagnostic_theme: data.diagnostic_theme || 'diagnostic-cyan',
      };
      setSavedHospitalThemeKey(next.active_theme);
      setSavedBloodBankThemeKey(next.blood_bank_theme);
      setSavedDiagnosticThemeKey(next.diagnostic_theme);
      // First hydration: also push saved → applied so visitors see the correct theme.
      // Subsequent refreshes do NOT touch applied state, so admin live-preview is preserved.
      if (!hydratedRef.current) {
        setHospitalThemeKey(next.active_theme);
        setBloodBankThemeKey(next.blood_bank_theme);
        setDiagnosticThemeKey(next.diagnostic_theme);
        hydratedRef.current = true;
      }
      writeCache(next);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Theme refresh from server failed:', err);
      }
    }
  }, []);

  useEffect(() => { refreshFromServer(); }, [refreshFromServer]);

  // Listen for admin-triggered theme refresh (e.g. after another tab saved a new theme)
  useEffect(() => {
    const handler = (e) => {
      // If event carries explicit themes (same-tab save), apply directly to skip a roundtrip
      if (e && e.detail) {
        const { active_theme, blood_bank_theme, diagnostic_theme } = e.detail;
        if (active_theme) {
          setSavedHospitalThemeKey(active_theme);
          setHospitalThemeKey(active_theme);
        }
        if (blood_bank_theme) {
          setSavedBloodBankThemeKey(blood_bank_theme);
          setBloodBankThemeKey(blood_bank_theme);
        }
        if (diagnostic_theme) {
          setSavedDiagnosticThemeKey(diagnostic_theme);
          setDiagnosticThemeKey(diagnostic_theme);
        }
        writeCache({
          active_theme: active_theme || savedHospitalThemeKey,
          blood_bank_theme: blood_bank_theme || savedBloodBankThemeKey,
          diagnostic_theme: diagnostic_theme || savedDiagnosticThemeKey,
        });
      } else {
        refreshFromServer();
      }
    };
    window.addEventListener('skb:theme-changed', handler);
    return () => window.removeEventListener('skb:theme-changed', handler);
  }, [refreshFromServer, savedHospitalThemeKey, savedBloodBankThemeKey, savedDiagnosticThemeKey]);

  // Live-preview helpers used by the admin Theme Manager.
  // These ONLY update "applied" state, so the dirty check (pending vs saved) keeps Save enabled.
  const previewHospital = useCallback((key) => setHospitalThemeKey(key), []);
  const previewBloodBank = useCallback((key) => setBloodBankThemeKey(key), []);
  const previewDiagnostic = useCallback((key) => setDiagnosticThemeKey(key), []);

  // Called after a successful PUT /api/admin/settings — promotes applied -> saved.
  const commitSavedThemes = useCallback(({ hospital, bloodBank, diagnostic }) => {
    if (hospital) setSavedHospitalThemeKey(hospital);
    if (bloodBank) setSavedBloodBankThemeKey(bloodBank);
    if (diagnostic) setSavedDiagnosticThemeKey(diagnostic);
    writeCache({
      active_theme: hospital || savedHospitalThemeKey,
      blood_bank_theme: bloodBank || savedBloodBankThemeKey,
      diagnostic_theme: diagnostic || savedDiagnosticThemeKey,
    });
  }, [savedHospitalThemeKey, savedBloodBankThemeKey, savedDiagnosticThemeKey]);

  const value = {
    // applied (currently rendering)
    hospitalThemeKey,
    bloodBankThemeKey,
    diagnosticThemeKey,
    // saved (server-stored)
    savedHospitalThemeKey,
    savedBloodBankThemeKey,
    savedDiagnosticThemeKey,
    // helpers
    previewHospital,
    previewBloodBank,
    previewDiagnostic,
    commitSavedThemes,
    refreshFromServer,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

// Wrap any subtree to apply an alternate theme (for Blood Bank / Diagnostic pages).
export const ThemedSection = ({ themeKey, className = '', children, ...rest }) => {
  const theme = getTheme(themeKey);
  const style = {};
  Object.entries(theme.palette).forEach(([k, v]) => { style[`--${k}`] = v; });
  return (
    <div className={className} style={style} {...rest}>
      {children}
    </div>
  );
};

export default ThemeContext;
